import { useCallback, useEffect, useRef, useState } from 'react';
import { designSearchApi } from '../lib/api.js';

/**
 * 成果物サムネイルの GCS 永続コピーを Cookie 認証付き fetch で取得し、object URL を返す。
 *
 * `<img src>` の直参照は Edge Router がサブリソースを非認証扱いにして OAuth へ 302 リダイレクト
 * するため閲覧者のセッション状態次第で壊れる。認証経路の fetch で Blob を取得し、object URL 化して
 * `<img>` に渡すことでこれを回避する。取得できない（キー無し・認証失敗・404・非画像）ときは
 * `isError` を立て、呼び出し側は drive サムネイルへフォールバックする。object URL は unmount /
 * キー変更時に revoke する。
 *
 * fetch 化で失われる `<img loading="lazy">` の遅延読み込みを補うため、返す `ref` を対象要素に
 * 付けると IntersectionObserver でカードが視界（付近）に入るまで fetch を遅延する。これにより
 * 検索直後に画面外の結果まで全件並列 fetch する回帰を避ける。IntersectionObserver 非対応環境では
 * 遅延せず即 fetch する。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 成果物サムネイルを認証付き fetch→Blob→object URL 化して返すフック。視界に入るまで遅延読み込みし取得不可は isError で通知
 * @graph-connects designSearchApi.fetchThumbnailBlob [calls] サムネイル GCS プロキシの Blob 取得
 */
export function useDesignThumbnail(objectKey: string | null | undefined): {
  objectUrl: string | null;
  isError: boolean;
  ref: (node: Element | null) => void;
} {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);
  const [visible, setVisible] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // カードが視界（付近）に入ったら一度だけ visible を立てる遅延読み込みゲート。
  // ref は useCallback で同一参照を保つため、対象要素の mount で node、unmount で null が渡る。
  const ref = useCallback((node: Element | null) => {
    if (node === null) {
      observerRef.current?.disconnect();
      observerRef.current = null;
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      // 非対応環境（SSR / 旧ブラウザ）では遅延せず即読み込む。
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        setVisible(true);
        observer.disconnect();
        observerRef.current = null;
      },
      { rootMargin: '200px' },
    );
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  useEffect(() => {
    if (objectKey === null || objectKey === undefined || objectKey === '') {
      setObjectUrl(null);
      setIsError(false);
      return;
    }
    if (!visible) return;

    let active = true;
    let createdUrl: string | null = null;
    setObjectUrl(null);
    setIsError(false);

    designSearchApi
      .fetchThumbnailBlob(objectKey)
      .then((blob) => {
        if (!active) return;
        createdUrl = URL.createObjectURL(blob);
        setObjectUrl(createdUrl);
      })
      .catch(() => {
        if (!active) return;
        setIsError(true);
      });

    return () => {
      active = false;
      if (createdUrl !== null) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [objectKey, visible]);

  return { objectUrl, isError, ref };
}
